<template>
  <!--
    Render only when:
      (a) on a path under `visibleUnder` (default /admin), AND
      (b) we have content — loading OR ≥1 group with ≥1 tile.
    An empty load doesn't reserve the 220px column; nothing shows at all.
    A body class `admin-subnav-active` toggles in sync so the content-area
    push lives in CSS without inline-style juggling.
  -->
  <aside v-if="shouldRender" class="admin-subnav" aria-label="Settings navigation">
    <div v-if="loading" class="admin-subnav__loading">
      <CygnusIcon name="spinner" spin />
    </div>

    <div v-else>
      <div v-for="group in groups" :key="group.key" class="admin-subnav__group">
        <div class="admin-subnav__group-title">{{ group.title }}</div>
        <router-link
          v-for="tile in group.tiles"
          :key="tile.route"
          :to="tile.route"
          class="admin-subnav__link"
          active-class="is-active"
          :exact-active-class="'is-active'"
        >
          <CygnusIcon :name="tile.icon || 'gear'" />
          <span class="admin-subnav__label">{{ tile.title }}</span>
        </router-link>
      </div>
    </div>
  </aside>
</template>

<script>
/**
 * AdminSubnav (icarus-theme-base) — fixed-position secondary column rendered
 * only when the current route is under /admin/*. Mirrors the per-section
 * sub-nav pattern but exclusively for /admin/* — pulls its tile data from
 * a conventional shell-module endpoint so a single backend registration
 * drives both the dashboard tile grid + this column.
 *
 * Convention: consumer's shell module registers `GET /api/v1/shell/admin/settings-tiles`
 * returning `{ groups: [{ key, title, tiles: [{ route, title, icon }] }] }`.
 * Missing endpoint = silent empty render (no console noise).
 *
 * Endpoint is overridable via prop; default matches the convention.
 */
import useApi from '@apexl/icarus-core/src/services/api';
import CygnusIcon from '@apexl/icarus-core/src/components/icons/CygnusIcon.vue';

export default {
  name: 'AdminSubnav',
  components: { CygnusIcon },
  props: {
    /** Endpoint that returns the admin tile groups. Override for non-standard layouts. */
    endpoint: { type: String, default: '/api/v1/shell/admin/settings-tiles' },
    /** Route prefix that triggers visibility. Default: `/admin`. */
    visibleUnder: { type: String, default: '/admin' },
  },
  data() {
    return {
      groups: [],
      loading: false,
      hasFetched: false,
    };
  },
  computed: {
    visible() {
      return (this.$route?.path || '').startsWith(this.visibleUnder);
    },
    hasContent() {
      return this.groups.some(g => Array.isArray(g.tiles) && g.tiles.length > 0);
    },
    shouldRender() {
      return this.visible && (this.loading || this.hasContent);
    },
  },
  watch: {
    visible(now) {
      if (now && !this.hasFetched) this.load();
      this.syncBodyClass();
    },
    shouldRender() {
      this.syncBodyClass();
    },
  },
  mounted() {
    if (this.visible) this.load();
    this.syncBodyClass();
  },
  beforeUnmount() {
    document.body.classList.remove('admin-subnav-active');
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await useApi().get(this.endpoint);
        this.groups = res?.groups || [];
        this.hasFetched = true;
      } catch {
        this.groups = []; // silent — endpoint may not be registered (yet)
      } finally {
        this.loading = false;
        this.syncBodyClass();
      }
    },
    syncBodyClass() {
      document.body.classList.toggle('admin-subnav-active', this.shouldRender);
    },
  },
};
</script>

<style lang="scss">
// Fixed secondary column. Sits at left:72px (right of the icon strip) and
// spans the full viewport height. Visual treatment is a glassy gradient
// over the theme sidebar background.
.admin-subnav {
  position: fixed;
  left: 72px;
  top: 0;
  bottom: 0;
  width: 220px;
  height: 100vh;
  overflow-y: auto;
  z-index: 1040;
  background:
    linear-gradient(180deg,
      var(--theme-accent-light, rgba(255, 255, 255, 0.10)) 0%,
      transparent 38%,
      rgba(0, 0, 0, 0.18) 100%),
    radial-gradient(circle at 20% 12%,
      rgba(255, 255, 255, 0.08) 0%,
      transparent 55%),
    var(--theme-bg-sidebar);
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 6px 0 24px rgba(0, 0, 0, 0.18);
  // Clears the brand + LhsHeaderCard stack at the top of the sidebar.
  padding: 190px 0 1rem 0;
}

.admin-subnav__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
}

.admin-subnav__group { padding: 6px 0 4px; }

.admin-subnav__group-title {
  font-family: var(--theme-font-display, system-ui);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  padding: 12px 1.25rem 6px;
}

.admin-subnav__link {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.5rem 1.25rem;
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
  text-decoration: none;
  border-left: 2px solid transparent;
  transition: background 0.12s, color 0.12s, border-color 0.12s;

  i, .cygnus-icon {
    width: 1rem;
    text-align: center;
    font-size: 0.9rem;
    opacity: 0.7;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--theme-accent-light, #fff);
  }

  &.is-active {
    background: var(--theme-accent-light, rgba(255, 255, 255, 0.14));
    color: var(--theme-text-sidebar-active, #fff);
    border-left-color: var(--theme-accent-primary);
    i, .cygnus-icon { opacity: 1; }
  }
}

// When AdminSubnav is actively rendered, push the LHS sidebar right by
// 220px so it sits to the right of the fixed subnav column. The body
// class is toggled by AdminSubnav itself (syncBodyClass()), so this rule
// only fires when content is actually showing — empty groups don't
// reserve the column.
body.admin-subnav-active #leftSidebar {
  margin-right: 220px;
}
</style>
